import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useCompanyContext } from '../../../../context/CompanyContext';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import {
  fecthCBProgressWorksheet,
  fetchPBBillGroups,
  getSubmitedProgressBilling,
} from '../../../../services/accounting/ContractBillingsService';
import ContractProgressTable from './ContractProgressTable';
import { useFormContext } from 'react-hook-form';
import { fetchContractBillingFilters } from '../CBFilters';
import { useRolesAccessContext } from '../../../../context/RolesAccessContext';
import ProgressBillingProcess from './ProgressBillingProcess';
import {
  BillingDraft,
  ContractProgressItem,
  ProgressBillingDraft,
} from '../../../../Interfaces/Accounting/ContractBillings.interface';
import ProgressBillingLastApproved from './ProgressBillingLastApproved';
import CalendarMonthStyled from '../../../../components/inputs/StyledInputs/CalendarMonthStyled';
import { roundCurrency } from '@/src/utils/formatUtils';
import ApprovalHistory from '../../../../components/approvals/ApprovalHistory';
import { UseDefaultContext } from '../../../../context/UseDefaultContext';
import DropdownStyled from '../../../../components/inputs/StyledInputs/DropdownStyled';
import { EMPTY_VALUE } from '@/src/utils/cbConst';

const ContractProgressTableContainer = () => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const { useDefault } = UseDefaultContext();
  const { watch } = useFormContext<fetchContractBillingFilters>();
  const filters = watch();
  const [showSMValues, setShowSMValues] = useState(false);
  const [hideSMCheckbox, setHideSMCheckbox] = useState(false);
  const [items, setItems] = useState<ContractProgressItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, BillingDraft>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const isEnabled = selectedCompany && filters.contract;
  const access = rolesAcess?.find(
    (access) => access.report === 'contract_progress_worksheet'
  );
  const selectedDraft: BillingDraft | undefined =
    drafts[filters.contract ?? ''];

  const completedItems = (
    items: ContractProgressItem[],
    amounts: Record<number, number>,
    smAmounts: BillingDraft['smAmounts']
  ) => {
    return items.map((item) => {
      const index = parseInt(item.Item);
      const smValues = (smAmounts ?? {})[index];
      const remainingSM =
        smValues?.purchaseSM || smValues?.productionSM
          ? roundCurrency(
              (item.SM ?? 0) +
                (smValues.purchaseSM ?? 0) -
                (smValues.productionSM ?? 0)
            ) ?? 0
          : undefined;
      return {
        ...item,
        NewBilling: amounts[index] ?? null,
        currentPurchasedSM: smValues?.purchaseSM,
        currentProductionSM: smValues?.productionSM,
        remainingSM,
      };
    });
  };

  const billGroupRequest = useQuery({
    queryKey: [
      'getCBProgressBillGroups',
      selectedCompany,
      filters.contract,
      drafts[filters.contract!]?.billMonth,
    ],
    queryFn: ({ signal }) => {
      return fetchPBBillGroups(
        selectedCompany!.id,
        filters.contract ?? '',
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!isEnabled,
  });

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [
      'getCBProgress',
      selectedCompany,
      filters.contract,
      filters.itemsState,
      filters.submittedID,
      filters.info,
    ],
    queryFn: ({ signal }) => {
      setIsProcessing(true);

      return fecthCBProgressWorksheet(
        {
          companyID: selectedCompany!.id,
          contract: filters.contract ?? '',
          edit: filters.submittedID ? undefined : !!access?.editable,
          draftID: filters.submittedID ?? undefined,
          hideBilled: filters.itemsState === 'hide' ? true : false,
          info: filters.info ?? undefined,
          billGroup: drafts[filters.contract!]?.billGroup,
        },
        signal
      );
    },
    onSuccess: (data) => {
      const draft = drafts[filters.contract!] ?? {};
      const contractLastUpdates = draft.updatedAt;
      const draftUpdate = new Date(data.draft?.updatedAt);

      const hasSM = data.items.some((item: ContractProgressItem) => item.SM);
      setHideSMCheckbox(hasSM);

      if (
        !contractLastUpdates ||
        contractLastUpdates?.getTime() < draftUpdate?.getTime()
      ) {
        setDrafts((drafts) => {
          const draft = drafts[filters.contract!] ?? {};
          draft.billing = data.draft?.amounts;
          draft.retainage = data.draft?.releasedRetainage;
          draft.smAmounts = data.draft?.smAmounts;
          draft.approvals = data.draft?.approvals;
          draft.lastStep = data.draft?.lastStep;
          if (data.draft?.billMonth) {
            draft.billMonth = new Date(data.draft?.billMonth);
          } else {
            delete draft.billMonth;
          }

          if (data.draft?.billGroup) {
            draft.billGroup = data.draft?.billGroup;
          } else {
            delete draft.billGroup;
          }

          draft.updatedAt = null;
          drafts[filters.contract!] = draft;
          return drafts;
        });

        setShowSMValues(hasSM || !!data.draft?.smAmounts);
        setItems(data.items);
      } else {
        setItems(
          completedItems(data.items, draft.billing ?? {}, draft.smAmounts)
        );

        setShowSMValues(hasSM || !!draft?.smAmounts);
      }
      setIsProcessing(false);
    },
    onError: () => {
      setIsProcessing(false);
    },
    refetchOnWindowFocus: false,
    enabled: !!isEnabled,
  });

  const UpdateTime = () => {
    setDrafts((drafts) => {
      drafts[filters.contract!].updatedAt = new Date();
      return drafts;
    });
  };

  const changeBillingDraft = (item: number, value?: number) => {
    setDrafts((drafts) => {
      const newBillings: Record<number, number> =
        drafts[filters.contract!].billing ?? {};

      if (value) {
        newBillings[item] = value;
        drafts[filters.contract!].billing = newBillings;
      } else if (newBillings[item]) {
        delete newBillings[item];
      } else {
        return drafts;
      }

      drafts[filters.contract!].billing = newBillings;
      drafts[filters.contract!] = { ...drafts[filters.contract!] };
      return drafts;
    });

    UpdateTime();
  };

  const changePurchasedSMDraft = (item: number, value?: number) => {
    setDrafts((drafts) => {
      const smAmounts = drafts[filters.contract!].smAmounts ?? {};

      if (value) {
        smAmounts[item] = {
          purchaseSM: value,
          productionSM: smAmounts[item]?.productionSM,
        };
      } else if (smAmounts[item]?.purchaseSM) {
        smAmounts[item].purchaseSM = undefined;
        if (!smAmounts[item].productionSM) {
          delete smAmounts[item];
        }
      } else {
        return drafts;
      }

      drafts[filters.contract!].smAmounts = smAmounts;
      drafts[filters.contract!] = { ...drafts[filters.contract!] };
      return drafts;
    });

    UpdateTime();
  };

  const changeProductionSMDraft = (item: number, value?: number) => {
    setDrafts((drafts) => {
      const smAmounts = drafts[filters.contract!].smAmounts ?? {};

      if (value) {
        smAmounts[item] = {
          purchaseSM: smAmounts[item]?.purchaseSM,
          productionSM: value,
        };
      } else if (smAmounts[item]?.productionSM) {
        smAmounts[item].productionSM = undefined;
        if (!smAmounts[item].purchaseSM) {
          delete smAmounts[item];
        }
      } else {
        return drafts;
      }

      drafts[filters.contract!].smAmounts = smAmounts;
      drafts[filters.contract!] = { ...drafts[filters.contract!] };
      return drafts;
    });

    UpdateTime();
  };

  const changeRetainage = (number?: number) => {
    setDrafts((drafts) => {
      drafts[filters.contract!].retainage = number;
      drafts[filters.contract!] = { ...drafts[filters.contract!] };
      return drafts;
    });

    UpdateTime();
  };

  const changeBillMonth = (billMonth?: Date) => {
    setDrafts((drafts) => {
      drafts[filters.contract!].billMonth = billMonth;
      drafts[filters.contract!] = { ...drafts[filters.contract!] };
      return { ...drafts };
    });

    UpdateTime();
  };

  const changeBillGroup = (billGroup?: string) => {
    setDrafts((drafts) => {
      drafts[filters.contract!] = {
        ...drafts[filters.contract!],
        billing: {},
        smAmounts: {},
        billGroup,
      };
      return { ...drafts };
    });

    setItems((items) => {
      const newItems = items.map((item) => {
        delete item.NewBilling;
        delete item.currentPurchasedSM;
        delete item.currentProductionSM;
        delete item.remainingSM;
        return item;
      });

      return newItems;
    });

    UpdateTime();
    refetch();
  };

  const resetDraft = () => {
    setDrafts((drafts) => {
      const draft = drafts[filters.contract!];
      drafts[filters.contract!] = {
        updatedAt: new Date(),
        billing: {},
        smAmounts: {},
        approvals: draft.approvals,
        lastStep: draft.lastStep,
        billGroup: draft.billGroup ? EMPTY_VALUE : undefined,
      };
      return { ...drafts };
    });

    setItems((items) => {
      const newItems = items.map((item) => {
        delete item.NewBilling;
        delete item.currentPurchasedSM;
        delete item.currentProductionSM;
        delete item.remainingSM;
        return item;
      });

      return newItems;
    });

    refetch();
  };

  const submittedPBs = useQuery({
    queryKey: [
      'getSubmittedProgressBillings',
      selectedCompany,
      filters.contract,
      useDefault,
    ],
    queryFn: ({ signal }) =>
      getSubmitedProgressBilling(
        selectedCompany!.id,
        filters.contract ?? '',
        signal
      ),

    refetchOnWindowFocus: false,
    enabled: false,
  });

  const afterApproval = (draftID: string) => {
    submittedPBs.data?.forEach(
      (draft: ProgressBillingDraft & { disabled?: boolean }) => {
        if (draft.id === draftID) {
          draft.disabled = true;
        }
      }
    );
  };

  if (!isEnabled) {
    return (
      <div className="mt-4">
        <ErrorMessage
          severity="info"
          content={'To start the search please select a Job.'}
        />
      </div>
    );
  }

  if (isFetching || isProcessing)
    return (
      <div className="text-center mx-auto mt-3">
        <ProgressSpinner />
      </div>
    );

  if (isError)
    return (
      <ErrorMessage
        content={'Failed to obtain data! Please try again later.'}
      />
    );

  return (
    <div className="pdfDivFitContent">
      {!!access?.editable &&
        selectedCompany?.isActive &&
        !filters.submittedID && (
          <div className="ml-3 mt-4">
            <ProgressBillingLastApproved onSuccess={refetch} />
          </div>
        )}
      {((selectedCompany?.isActive &&
        (filters.submittedID ? access?.shouldApprove : access?.editable)) ||
        selectedDraft?.approvals) && (
        <div className="flex flex-row justify-content-between mx-3 mt-3 mb-1 gap-1 align-content-center">
          {selectedCompany?.isActive &&
            (filters.submittedID
              ? access?.shouldApprove
              : access?.editable) && (
              <div className="flex flex-row gap-3 flex-wrap">
                <div>
                  <label className="text-standard h-fit my-auto">
                    Bill Month
                  </label>
                  <CalendarMonthStyled
                    readOnlyInput
                    showButtonBar
                    todayButtonClassName="hidden"
                    clearButtonClassName="mx-auto p-button-secondary"
                    placeholder="Select Month"
                    inputId="invDate"
                    value={drafts[filters.contract!]?.billMonth}
                    onChange={(event) =>
                      event.target.value
                        ? changeBillMonth(event.target.value as Date)
                        : changeBillMonth(undefined)
                    }
                  />
                </div>
                {billGroupRequest.data?.length > 1 && (
                  <div>
                    <label className="text-standard h-fit my-auto">
                      Bill Group
                    </label>
                    <DropdownStyled
                      value={
                        selectedDraft?.billGroup === EMPTY_VALUE
                          ? undefined
                          : selectedDraft?.billGroup
                      }
                      options={billGroupRequest.data}
                      placeholder={
                        billGroupRequest.isError
                          ? 'Failed to load group names!'
                          : 'Select Group Name'
                      }
                      isDisabled={billGroupRequest.isError}
                      isLoading={billGroupRequest.isFetching}
                      clearable={true}
                      resetFilterOnHide={true}
                      onChange={(event) => {
                        if (event.target.value) {
                          changeBillGroup(event.target.value);
                        } else {
                          changeBillGroup(EMPTY_VALUE);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          {selectedDraft?.approvals ? (
            <React.Fragment>
              <div
                className="text-18px font-bold cursor-pointer totalColor hover:underline flex"
                onClick={() => setShowHistory(true)}
              >
                Check Draft Approvals
              </div>
              <ApprovalHistory
                show={showHistory}
                hide={() => {
                  setShowHistory(false);
                }}
                approvals={selectedDraft.approvals}
                lastStep={selectedDraft.lastStep ?? 0}
              />
            </React.Fragment>
          ) : undefined}
        </div>
      )}
      <ContractProgressTable
        items={items || []}
        releasedRetainage={drafts[filters.contract!]?.retainage ?? undefined}
        isApproval={!!filters.submittedID}
        access={access ?? { report: 'contract_progress_worksheet' }}
        updateIsEditing={setIsEditing}
        updateBillingDraft={changeBillingDraft}
        updateReleasedRetainage={changeRetainage}
        changePurchasedSMDraft={changePurchasedSMDraft}
        changeProductionSMDraft={changeProductionSMDraft}
        changeIncludeSmValues={(show) => setShowSMValues(show)}
        showSMValues={showSMValues}
        hideSMValuesCheckbox={hideSMCheckbox}
        blockSMValuesCheckbox={
          hideSMCheckbox ||
          !!Object.keys(drafts[filters.contract!]?.smAmounts ?? {}).length
        }
      />
      {selectedCompany?.isActive && (
        <div className="mt-3 flex gap-4 mx-3 printHide">
          <ProgressBillingProcess
            draft={drafts[filters.contract!]}
            defaultDraftID={data.draft?.id}
            isSubmitted={data.draft?.submitted}
            approvalMode={!!filters.submittedID}
            access={access ?? { report: 'contract_progress_worksheet' }}
            blockAll={isEditing}
            defaultBlockSave={!drafts[filters.contract!]?.updatedAt}
            resetDraft={resetDraft}
            afterApproval={afterApproval}
          />
        </div>
      )}
    </div>
  );
};
export default ContractProgressTableContainer;
